import {
    Autocomplete,
    Box,
    Checkbox,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Popper,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { autocompleteClasses } from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";

import CloseIcon from "@mui/icons-material/Close";

import LightTooltip from "../../ui/LightTooltip";
import BaseSelect2 from "../../ui/BaseSelect2";
import CustTextField from "../../ui/CustTextField";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme.palette.primary,
        fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        padding: "5px 15px",
    },
}));

const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: "border-box",
        "& ul": {
            padding: 0,
            margin: 0,
        },
    },
    [`& .${autocompleteClasses.paper}`]: {
        borderRadius: "15px",
    },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: "white",
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
    "& td": {
        border: 0,
    },
    "& td:first-of-type": {
        borderTopLeftRadius: 8,
        borderBottomLeftRadius: 8,
    },
    "& td:last-child": {
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
    },
}));

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: 310,
            width: 200,
        },
    },
};

const NewInvoiceTableItems = (props) => {
    const {
        rows,
        setRows,
        xeroTaxRates,
        accOptions,
        taxOptions,
        selected,
        setSelected,
        xeroItems,
        integration,
        conversion_type,
        trackingCategories,
        trackingConfig,
    } = props;

    const [t] = useTranslation("global");

    const todos = useSelector((state) => state.value);

    const columns = [
        { name: "item_code", title: t("register.code"), width: "15%" },
        { name: "description", title: t("accounts.description"), width: "20%" },
        { name: "quantity", title: t("converter.qty") },
        { name: "unit_price", title: t("payment.unitPrice") },
        { name: "account_code", title: t("accounts.accountNum"), width: "15%" },
        { name: "discount", title: t("converter.discount") },
        ...(!(integration === 2 && conversion_type === 2)
            ? [{ name: "tax_type", title: t("accounts.tax"), width: "15%" }]
            : []),
        // Add tracking categories if they exist
        ...(trackingConfig && trackingCategories.length > 0
            ? trackingCategories.map((category) => ({
                  name: category.tracking_category_id_xero,
                  title: category.tracking_category_name_xero,
              }))
            : []),
        { name: "line_amount", title: t("converter.totalAmount") },
        // { name: "actions", title: <></>, width: "8%" },
    ];

    const handleChangeRow = (event, id, name) => {
        let copyRows = rows.map(item => ({ ...item }));
        setRows(
            copyRows.map((aux) => {
                if (id === aux._id) {
                    return {
                        ...aux,
                        [name]: event.target.value,
                    };
                } else {
                    return aux;
                }
            })
        );
    };

    const getAmount = (unit_price, quantity, discount = 0, tax = 0) => {
        const subTotal = Number(unit_price) * Number(quantity);
        const taxRate = Number(xeroTaxRates.find((item) => item?.value === tax)?.taxRate || 0);
        const discountRate = Number(discount);

        const totalTax = subTotal * (taxRate / 100);
        const totalDiscount = subTotal * (discountRate / 100);

        return subTotal + totalTax - totalDiscount;
    };

    const handleChangeAmount = (value, id, blur = false) => {
        let newValue;
        if (blur) {
            newValue = value === "" ? 0 : Number(value);
        } else {
            newValue = value === "" ? "" : Number(value);
        }

        if (newValue >= 0 || newValue === "") {
            let copyRows = rows.map(item => ({ ...item }));
            setRows(
                copyRows.map((aux) => {
                    if (id === aux._id) {
                        return {
                            ...aux,
                            quantity: newValue,
                            amount: getAmount(
                                aux?.unit_price ?? 0,
                                newValue ?? 0,
                                aux?.discount ?? 0,
                                aux?.tax_type ?? 0
                            ),
                        };
                    } else {
                        return aux;
                    }
                })
            );
        }
    };

    const handleChangeUP = (value, id, blur = false) => {
        let newValue;
        if (blur) {
            newValue = value === "" ? 0 : Number(value);
        } else {
            newValue = value === "" ? "" : Number(value);
        }

        let copyRows = rows.map(item => ({ ...item }));
        setRows(
            copyRows.map((aux) => {
                if (id === aux._id) {
                    return {
                        ...aux,
                        unit_price: newValue,
                        amount: getAmount(
                            newValue ?? 0,
                            aux?.quantity ?? 0,
                            aux?.discount ?? 0,
                            aux?.tax_type ?? 0
                        ),
                    };
                } else {
                    return aux;
                }
            })
        );
    };

    const handleChangeSelect = (newVal, id) => {
        let copyRows = rows.map(item => ({ ...item }));
        const newRows = copyRows.map((aux) => {
            if (id === aux._id) {
                return {
                    ...aux,
                    account_code: newVal.value,
                    account_code_json: newVal,
                };
            } else {
                return aux;
            }
        });
        setRows(newRows);
    };

    const handleChangeDiscount = (value, id, blur = false) => {
        let newValue;
        if (blur) {
            newValue = value === "" ? 0 : Number(value);
        } else {
            newValue = value === "" ? "" : Number(value);
        }

        if (newValue >= 0 || newValue === "") {
            let copyRows = rows.map(item => ({ ...item }));
            setRows(
                copyRows.map((aux) => {
                    if (id === aux._id) {
                        return {
                            ...aux,
                            discount: newValue,
                            amount: getAmount(
                                aux?.unit_price ?? 0,
                                aux?.quantity ?? 0,
                                newValue ?? 0,
                                aux?.tax_type ?? 0
                            ),
                        };
                    } else {
                        return aux;
                    }
                })
            );
        }
    };

    const handleChangeSelectTax = async (newVal, id) => {
        const taxRate = xeroTaxRates.find((item) => item.value === newVal.value).taxRate;
        let copyRows = rows.map(item => ({ ...item }));
        const newRows = copyRows.map((aux) => {
            if (id === aux._id) {
                return {
                    ...aux,
                    tax_type: newVal.value,
                    tax_type_json: newVal,
                    tax_amount: (taxRate * (aux?.unit_price ?? 0 * aux?.quantity ?? 0)) / 100,
                    amount: getAmount(aux?.unit_price ?? 0, aux?.quantity ?? 0, aux?.discount ?? 0, newVal.value),
                };
            } else {
                return aux;
            }
        });
        setRows(newRows);
    };

    const handleDeleteRow = (id) => {
        let newRows = [];
        let copyRows = rows.map(item => ({ ...item }));

        if (copyRows.find((item) => item._id === id).new) {
            newRows = copyRows.filter((item) => item._id !== id);
        } else {
            newRows = copyRows.map((item) => {
                if (item._id === id) {
                    item.deleted = true;
                }
                return item;
            });
        }

        setRows(newRows);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            let copyRows = rows.map(item => ({ ...item }));
            const newSelected = copyRows.map((n) => n._id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClickCheck = (id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;

    const handleChangeSelectRow = (event, id) => {
        let newRows = [];

        let copyRows = rows.map(item => ({ ...item }));
        if (selected.length > 1 && selected.includes(id)) {
            newRows = copyRows.map((aux) => {
                if (selected.includes(aux._id)) {
                    aux.code = event.target.value;
                }
                return aux;
            });
        } else {
            newRows = copyRows.map((aux) => {
                if (id === aux._id) {
                    return {
                        ...aux,
                        code: event.target.value,
                    };
                } else {
                    return aux;
                }
            });
        }
        setRows(newRows);
    };

    const handleChangeTrackingCategory = (event, id, categoryId, indexCategory, categoryName) => {
        let copyRows = rows.map(item => ({ ...item }));
        let label = "tracking" + indexCategory;
        setRows(
            copyRows.map((aux) => {
                if (id === aux._id) {
                    return {
                        ...aux,
                        [label]: {
                            TrackingCategoryID: categoryId,
                            Option: categoryName,
                            TrackingOptionID: event.target.value,
                        },
                    };
                } else {
                    return aux;
                }
            })
        );
    };

    return (
        <TableContainer>
            <Table
                sx={{
                    borderCollapse: "separate",
                    borderSpacing: "0 6px",
                    minWidth: 1300,
                }}
            >
                <TableHead>
                    <TableRow
                        sx={{
                            "& th": { border: "none" },
                        }}
                    >
                        <StyledTableCell>
                            <Checkbox
                                color="primary"
                                indeterminate={selected.length > 0 && selected.length < rows.filter((item) => !item.deleted).length}
                                checked={rows.filter((item) => !item.deleted).length > 0 && selected.length === rows.filter((item) => !item.deleted).length}
                                onChange={handleSelectAllClick}
                            />
                        </StyledTableCell>
                        {columns.map((headCell) => (
                            <StyledTableCell
                                key={headCell.name}
                                align={headCell.align ?? "left"}
                                style={{
                                    width: headCell.width,
                                    lineHeight: "1.3",
                                }}
                            >
                                {headCell.title}
                            </StyledTableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows
                        .filter((item) => !item.deleted)
                        .map((row, index) => {
                            const isItemSelected = isSelected(row._id);
                            return (
                                <StyledTableRow key={row._id}>
                                    <StyledTableCell>
                                        <Checkbox
                                            color="primary"
                                            checked={isItemSelected}
                                            onClick={() => handleClickCheck(row._id)}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <BaseSelect2
                                            id="demo-simple-select-standard"
                                            name="code"
                                            value={row.code ?? ""}
                                            onChange={(event) => handleChangeSelectRow(event, row._id)}
                                            size="small"
                                            MenuProps={MenuProps}
                                            displayEmpty
                                            issearch
                                        >
                                            <MenuItem disabled value="">
                                                <em>{t("converter.chooseCode")}</em>
                                            </MenuItem>
                                            {xeroItems.map((item) => (
                                                <MenuItem value={item.value} key={item.value}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </BaseSelect2>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <CustTextField
                                            issearch
                                            fullWidth
                                            id="input-with-icon-textfield"
                                            value={row.description}
                                            name="description"
                                            onChange={(event) => handleChangeRow(event, row._id, "description")}
                                            size="small"
                                            error={!row.description}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <CustTextField
                                            issearch
                                            fullWidth
                                            id="input-with-icon-textfield"
                                            value={row.quantity}
                                            name="quantity"
                                            onChange={(event) => handleChangeAmount(event.target.value, row._id)}
                                            onBlur={() => {
                                                let finalValue = row.quantity ?? "";

                                                let formattedVal;
                                                // Si el valor está vacío, establecer a 0
                                                if (!finalValue || finalValue === "" || finalValue === 0) {
                                                    formattedVal = "";
                                                } else {
                                                    formattedVal = finalValue.toString()?.includes(".")
                                                        ? parseFloat(finalValue).toFixed(2)
                                                        : finalValue;
                                                }
                                                handleChangeAmount(formattedVal, row._id, true);
                                            }}
                                            size="small"
                                            type="number"
                                            inputProps={{ min: 1 }}
                                            error={!row.quantity || row.quantity === 0}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <CustTextField
                                            issearch
                                            id="input-with-icon-textfield"
                                            value={row.unit_price}
                                            name="unit_price"
                                            onChange={(event) => handleChangeUP(event.target.value, row._id)}
                                            size="small"
                                            type="number"
                                            sx={{
                                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                                    {
                                                        display: "none",
                                                    },
                                            }}
                                            onBlur={() => {
                                                let finalValue = row.unit_price ?? "";

                                                let formattedVal;
                                                // Si el valor está vacío, establecer a 0
                                                if (!finalValue || finalValue === "" || finalValue === 0) {
                                                    formattedVal = "";
                                                } else {
                                                    formattedVal = finalValue.toString()?.includes(".")
                                                        ? parseFloat(finalValue).toFixed(2)
                                                        : finalValue;
                                                }
                                                handleChangeUP(formattedVal, row._id, true);
                                            }}
                                            error={!row.unit_price || row.unit_price === 0}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Autocomplete
                                            disableClearable
                                            PopperComponent={StyledPopper}
                                            size="small"
                                            getOptionLabel={(option) =>
                                                option.name ? `${option.value} - ${option.name}` : ""
                                            }
                                            options={accOptions.sort(
                                                (a, b) => -b.suggestions.localeCompare(a.suggestions)
                                            )}
                                            value={row?.account_code_json || null}
                                            onChange={(event, newValue) => handleChangeSelect(newValue, row._id)}
                                            noOptionsText={
                                                <Box sx={{ bgcolor: "#e8e9ec", p: 0.8, borderRadius: 1 }}>
                                                    <Typography fontWeight={600} fontSize={13} mt={0.5} color="#131f3e">
                                                        {t("converter.noAccount")}
                                                    </Typography>
                                                </Box>
                                            }
                                            renderInput={(params) => (
                                                <CustTextField
                                                    error={!row.account_code}
                                                    issearch="true"
                                                    {...params}
                                                />
                                            )}
                                            groupBy={(option) => option.suggestions}
                                            renderGroup={(params) => (
                                                <li key={params.key}>
                                                    <Typography ml={1} variant="h3" gutterBottom>
                                                        {params.group}
                                                    </Typography>
                                                    <Divider />
                                                    <ul>{params.children}</ul>
                                                </li>
                                            )}
                                            isOptionEqualToValue={(option, value) => {
                                                if (!value || Object.keys(value).length === 0) return false;
                                                return option.value === value.value;
                                            }}
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <CustTextField
                                            issearch
                                            id="input-with-icon-textfield"
                                            value={row.discount}
                                            name="discount"
                                            onChange={(event) => handleChangeDiscount(event.target.value, row._id)}
                                            inputProps={{ min: 0.0, step: 0.01, max: 100 }}
                                            size="small"
                                            type="number"
                                            sx={{
                                                "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                                    {
                                                        display: "none",
                                                    },
                                            }}
                                            onBlur={() => {
                                                let finalValue = row.discount ?? "";

                                                let formattedVal;
                                                // Si el valor está vacío, establecer a 0
                                                if (!finalValue || finalValue === "" || finalValue === 0) {
                                                    formattedVal = "";
                                                } else {
                                                    formattedVal = finalValue.toString()?.includes(".")
                                                        ? parseFloat(finalValue).toFixed(2)
                                                        : finalValue;
                                                }
                                                handleChangeDiscount(formattedVal, row._id, true);
                                            }}
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                            }}
                                        />
                                    </StyledTableCell>
                                    {!(integration === 2 && conversion_type === 2) && (
                                        <StyledTableCell>
                                            <Autocomplete
                                                disableClearable
                                                PopperComponent={StyledPopper}
                                                size="small"
                                                getOptionLabel={(option) => option.name ?? ""}
                                                options={taxOptions.sort(
                                                    (a, b) => -b.suggestions.localeCompare(a.suggestions)
                                                )}
                                                value={row?.tax_type_json ?? null}
                                                onChange={(event, newValue) => handleChangeSelectTax(newValue, row._id)}
                                                noOptionsText={
                                                    <Box sx={{ bgcolor: "#e8e9ec", p: 0.8, borderRadius: 1 }}>
                                                        <Typography
                                                            fontWeight={600}
                                                            fontSize={13}
                                                            mt={0.5}
                                                            color="#131f3e"
                                                        >
                                                            {t("converter.noAccount")}
                                                        </Typography>
                                                    </Box>
                                                }
                                                renderInput={(params) => (
                                                    <CustTextField
                                                        error={integration === 1 && !row.tax_type}
                                                        issearch="true"
                                                        {...params}
                                                    />
                                                )}
                                                groupBy={(option) => option.suggestions}
                                                renderGroup={(params) => (
                                                    <li key={params.key}>
                                                        <Typography ml={1} variant="h3" gutterBottom>
                                                            {params.group}
                                                        </Typography>
                                                        <Divider />
                                                        <ul>{params.children}</ul>
                                                    </li>
                                                )}
                                                isOptionEqualToValue={(option, value) => {
                                                    if (!value || Object.keys(value).length === 0) return false;
                                                    return option.value === value.value;
                                                }}
                                            />
                                        </StyledTableCell>
                                    )}
                                    {trackingConfig &&
                                        trackingCategories.map((category, indexCategory) => (
                                            <StyledTableCell key={category.tracking_category_id_xero}>
                                                <BaseSelect2
                                                    id={`tracking-category-${category.tracking_category_id_xero}`}
                                                    name={category.tracking_category_name_xero}
                                                    value={row["tracking" + (indexCategory + 1)]?.TrackingOptionID ?? ""}
                                                    onChange={(event) =>
                                                        handleChangeTrackingCategory(
                                                            event,
                                                            row._id,
                                                            category.tracking_category_id_xero,
                                                            indexCategory + 1,
                                                            category.tracking_category_name_xero
                                                        )
                                                    }
                                                    size="small"
                                                    MenuProps={MenuProps}
                                                    issearch
                                                    customStyle={true}
                                                >
                                                    {category.tracking_category_options.map((option) => (
                                                        <MenuItem key={option.TrackingOptionID} value={option.TrackingOptionID}>
                                                            {option.Name}
                                                        </MenuItem>
                                                    ))}
                                                </BaseSelect2>
                                            </StyledTableCell>
                                        ))}
                                    <StyledTableCell>
                                        ${" "}
                                        {parseFloat(
                                            getAmount(
                                                row?.unit_price ?? 0,
                                                row?.quantity ?? 0,
                                                row?.discount ?? 0,
                                                row?.tax_type ?? 0
                                            )
                                        ).toLocaleString(todos.amountFormat, {
                                            minimumFractionDigits: 2,
                                        })}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {rows.filter((item) => !item.deleted).length > 1 && (
                                            <LightTooltip title={t("miscellaneous.delete")}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteRow(row._id)}
                                                    sx={{
                                                        color: "V030", // Color gris
                                                        "&:hover": {
                                                            color: "C100", // Cambia el color al hacer hover
                                                            cursor: "pointer", // Opcional: cambia el cursor a una mano
                                                        },
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </LightTooltip>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default NewInvoiceTableItems;
